'use client';

import {
  useCallback,
  useId,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { CutChooser, cutLabel } from '@/components/chrome/cut-chooser';
import { useDropdown } from '@/hooks/use-dropdown';
import { themeCuts, type ThemeCut } from '@/lib/theme-cuts';
import { cn } from '@/lib/utils';

/**
 * The header's cut control: a disclosure button showing the current cut's
 * swatch and name, which opens the vendored cut chooser in a panel below.
 *
 * Why a disclosure and not the chooser inline: the chrome header centres
 * its desktop nav with `lg:absolute lg:left-1/2`, so the actions row has to
 * fit to the right of it. Measured on the production build (2026-09-29):
 * the named chooser is 469px wide and collides with the nav at every width
 * from `lg` up, 1920px included, and a `compact` instance (284px) still
 * overlaps by 39px at 1280px and clears it only from 1366px. One 44px
 * trigger fits everywhere the row exists. The chooser itself stays the
 * verbatim registry item, mounted here unchanged; every cut is still named
 * in words beside its swatch, and the trigger names the current one.
 *
 * Disclosure semantics (WAI-ARIA): the button carries aria-expanded and
 * aria-controls, the panel follows it in DOM order so Tab moves from the
 * button to the checked option, Escape and a click outside close it (the
 * shared dropdown hook), and focus leaving the panel closes it too. The
 * panel stays open after a pick: arrow keys move the check and pick in one
 * step, so closing on change would strand a keyboard user after one press.
 * Escape returns focus to the button when focus was inside.
 */
export interface CutMenuProps {
  /** The cut the server stamped from the cookie; undefined for the base cut. */
  cut?: ThemeCut;
  /** Called after the chooser wrote the cookie and restamped <html>. */
  onChange?: (cut: string) => void;
  className?: string;
}

type SwatchStyle = CSSProperties & { '--swatch': string };

export function CutMenu({ cut, onChange, className }: CutMenuProps) {
  const [current, setCurrent] = useState<string>(cut ?? themeCuts.base);
  const panelId = useId();
  const dropdown = useDropdown();
  const { ref, isOpen, toggle, close } = dropdown;

  const caption = `${cutLabel(themeCuts.identity)} cut`;
  const swatch: SwatchStyle = {
    '--swatch': themeCuts.swatches[current as ThemeCut] ?? 'transparent',
  };

  const handleChange = useCallback(
    (next: string) => {
      setCurrent(next);
      onChange?.(next);
    },
    [onChange]
  );

  // Escape (handled by the hook on document) leaves focus on whatever option
  // had it, inside a panel that is about to unmount. Put it back on the
  // button. A click outside moves focus itself, so it is left alone.
  const returnFocus = useCallback(() => {
    const root = ref.current;
    if (root && root.contains(document.activeElement)) {
      root.querySelector<HTMLButtonElement>('[data-slot="cut-menu-trigger"]')?.focus();
    }
  }, [ref]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape' && isOpen) returnFocus();
    },
    [isOpen, returnFocus]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null;
      if (isOpen && next && !ref.current?.contains(next)) close();
    },
    [close, isOpen, ref]
  );

  return (
    <div
      ref={ref}
      data-slot="cut-menu"
      className={cn('relative', className)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <button
        type="button"
        data-slot="cut-menu-trigger"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={toggle}
        className={cn(
          'touch-target gap-2 rounded-md px-2 hover:bg-accent',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'transition-base'
        )}
      >
        <span
          aria-hidden="true"
          style={swatch}
          className="block size-4 shrink-0 rounded-full bg-[var(--swatch)] forced-color-adjust-none ring-1 ring-border"
        />
        <span className="sr-only">{caption}: </span>
        <span className="text-sm">{cutLabel(current)}</span>
        <ChevronDown
          className={cn('h-4 w-4 transition-fast', isOpen && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          id={panelId}
          data-slot="cut-menu-panel"
          className={cn(
            'absolute top-full right-0 mt-2 z-50 rounded-lg border bg-card p-1.5 shadow-xl',
            // The chooser lays its options in a wrapping row; in the panel
            // they stack, one named option per line, swatches aligned.
            '[&_[role=radiogroup]]:flex-col [&_[role=radiogroup]]:items-stretch [&_[role=radio]]:justify-start'
          )}
        >
          <CutChooser
            identity={themeCuts.identity}
            base={themeCuts.base}
            cuts={themeCuts.cuts}
            swatches={themeCuts.swatches}
            value={current}
            onChange={handleChange}
          />
        </div>
      )}
    </div>
  );
}
